// -*- coding: utf-8 -*-
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { apiUrl, fetchJson } from '../../apiBase';

const TZ = 'Europe/Istanbul';
const HOURS = Array.from({ length: 24 }).map((_, i) => String(i).padStart(2, '0'));
const DAY_OPTIONS = [
  { key: 'mon', label: 'Pzt' },
  { key: 'tue', label: 'Sal' },
  { key: 'wed', label: 'Çar' },
  { key: 'thu', label: 'Per' },
  { key: 'fri', label: 'Cum' },
  { key: 'sat', label: 'Cmt' },
  { key: 'sun', label: 'Paz' },
];
const REMINDER_TYPES = [
  {
    key: 'daily_activity',
    label: 'Günlük Yoklama/Ödev',
    help: 'Öğretmenin o gün yoklama veya ödev girişi yapmadığı sınıflar için hatırlatma gönderir.',
    template: 'daily_teacher_reminder_v2',
    scheduleLocked: false,
  },
  {
    key: 'weekly_class_summary',
    label: 'Haftalık Sınıf Özeti',
    help: 'Öğretmenin her sınıfı için geçen haftanın özetini; aktif öğrenci, soru, kitap, VKS ve haftanın öne çıkan öğrencileriyle gönderir.',
    template: 'teacher_weekly_class_summary_reminder_v1',
    scheduleLocked: true,
    lockedDays: ['mon'],
  },
];

const typeLabel = (key) => REMINDER_TYPES.find((item) => item.key === key)?.label || key;

export default function TeacherReminderSettings({ schoolId, onError, onSuccess }) {
  const [teachers, setTeachers] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [reminderType, setReminderType] = useState('daily_activity');
  const [hour, setHour] = useState('17');
  const [enabled, setEnabled] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState(["mon", "tue", "wed", "thu", "fri"]);

  const loadTeachers = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`));
      if (res.ok) {
        const data = await res.json();
        setTeachers(Array.isArray(data) ? data : data.teachers || []);
      }
    } catch (e) {
      onError && onError('Öğretmen listesi yüklenemedi');
    }
  }, [schoolId, onError]);

  const loadReminders = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/teacher-reminders/${encodeURIComponent(schoolId)}/items`));
      if (!res.ok) throw new Error('load');
      const data = await res.json();
      setReminders(data.reminders || []);
    } catch (e) {
      onError && onError('Hatırlatmalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [schoolId, onError]);

  useEffect(() => {
    loadTeachers();
    loadReminders();
  }, [loadTeachers, loadReminders]);

  const teacherOptions = useMemo(
    () =>
      (teachers || []).map((t) => ({
        ...t,
        label: `${t.name || ''} ${t.surname || ''}`.trim() || t.id,
        id: t.id,
      })),
    [teachers],
  );

  const selectedReminderMeta = useMemo(
    () => REMINDER_TYPES.find((item) => item.key === reminderType) || REMINDER_TYPES[0],
    [reminderType],
  );
  const scheduleLocked = Boolean(selectedReminderMeta?.scheduleLocked);
  const effectiveSelectedDays = scheduleLocked
    ? (selectedReminderMeta?.lockedDays || ['mon'])
    : selectedDays;

  const resetForm = () => {
    setSelectedTeacher(null);
    setReminderType('daily_activity');
    setHour('17');
    setEnabled(true);
    setEditingId(null);
    setEditDialogOpen(false);
    setSelectedDays(["mon", "tue", "wed", "thu", "fri"]);
  };

  const handleSave = async () => {
    if (!schoolId || !selectedTeacher || !hour) return;
    setSaving(true);
    try {
      const body = {
        teacher_id: selectedTeacher.id,
        reminder_type: reminderType,
        time: `${hour}:00`,
        enabled,
        days: effectiveSelectedDays,
      };
      let res;
      if (editingId) {
        res = await fetchJson(apiUrl(`/admin/teacher-reminders/${encodeURIComponent(schoolId)}/items/${encodeURIComponent(editingId)}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetchJson(apiUrl(`/admin/teacher-reminders/${encodeURIComponent(schoolId)}/items`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) throw new Error('save');
      const data = await res.json();
      setReminders(data.reminders || []);
      onSuccess && onSuccess(editingId ? 'Hatırlatma güncellendi' : 'Hatırlatma eklendi');
      resetForm();
    } catch (e) {
      onError && onError('Hatırlatma kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!schoolId || !id) return;
    setSaving(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/teacher-reminders/${encodeURIComponent(schoolId)}/items/${encodeURIComponent(id)}`), {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('delete');
      const data = await res.json();
      setReminders(data.reminders || []);
      onSuccess && onSuccess('Hatırlatma silindi');
    } catch (e) {
      onError && onError('Hatırlatma silinemedi');
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleToggle = async (id, value) => {
    const row = reminders.find((r) => String(r.id) === String(id));
    if (!row) return;
    setSaving(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/teacher-reminders/${encodeURIComponent(schoolId)}/items/${encodeURIComponent(id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacher_id: row.teacher_id,
          reminder_type: row.reminder_type || 'daily_activity',
          time: formatTime(row),
          enabled: value,
          days: row.days || ["mon", "tue", "wed", "thu", "fri"],
        }),
      });
      if (!res.ok) throw new Error('toggle');
      const data = await res.json();
      setReminders(data.reminders || []);
    } catch (e) {
      onError && onError('Durum güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    const teacher = teacherOptions.find((t) => t.id === row.teacher_id);
    setSelectedTeacher(teacher || null);
    setReminderType(row.reminder_type || 'daily_activity');
    setHour(formatTime(row).slice(0, 2));
    setEnabled(Boolean(row.enabled));
    setSelectedDays(row.days || ["mon", "tue", "wed", "thu", "fri"]);
    setEditDialogOpen(true);
  };

  const formatTime = (row) => {
    const h = String(row.hour ?? row.time?.split(':')[0] ?? '0').padStart(2, '0');
    return `${h}:00`;
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Öğretmen Hatırlatmaları
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Seçtiğiniz tipe göre öğretmene otomatik hatırlatma gönderilir.
          </Typography>
        </Box>

        <Alert severity="info">
          Hatırlatmalar seçtiğiniz saatte ({TZ}) otomatik gönderilir. Mesaj şablonu{' '}
          <strong>{selectedReminderMeta.template}</strong>.
          {scheduleLocked ? ' Bu tipte gün seçimi sistem tarafından Pazartesi olarak sabitlenir.' : ''}
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.6fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            select
            label="Hatırlatma tipi"
            value={reminderType}
            SelectProps={{ native: true }}
            onChange={(e) => {
              const nextType = e.target.value;
              setReminderType(nextType);
              const nextMeta = REMINDER_TYPES.find((item) => item.key === nextType);
              if (nextMeta?.scheduleLocked) {
                setSelectedDays(nextMeta.lockedDays || ['mon']);
              }
            }}
          >
            {REMINDER_TYPES.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </TextField>
          <Autocomplete
            options={teacherOptions}
            value={selectedTeacher}
            onChange={(_, val) => setSelectedTeacher(val)}
            getOptionLabel={(opt) => opt?.label || ''}
            renderInput={(params) => <TextField {...params} label="Öğretmen seçin" />}
          />
          <TextField
            select
            label="Saat (sadece saat başı)"
            value={hour}
            SelectProps={{ native: true }}
            onChange={(e) => setHour(e.target.value)}
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {h}:00
              </option>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <Typography>Hatırlatmayı etkinleştir</Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {selectedReminderMeta.help}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {DAY_OPTIONS.map((d) => {
            const active = effectiveSelectedDays.includes(d.key);
            return (
              <Chip
                key={d.key}
                label={d.label}
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                disabled={scheduleLocked}
                onClick={() => {
                  setSelectedDays((prev) =>
                    prev.includes(d.key) ? prev.filter((x) => x !== d.key) : [...prev, d.key]
                  );
                }}
              />
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" disabled={scheduleLocked} onClick={() => setSelectedDays(["mon", "tue", "wed", "thu", "fri"])}>
            Hafta içi
          </Button>
          <Button variant="outlined" size="small" disabled={scheduleLocked} onClick={() => setSelectedDays(["mon", "tue", "wed", "thu", "fri", "sat", "sun"])}>
            Tüm hafta
          </Button>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {editingId && (
            <Button onClick={resetForm} variant="text">
              Vazgeç
            </Button>
          )}
          <Button startIcon={editingId ? <EditIcon /> : <AddIcon />} variant="contained" disabled={saving} onClick={handleSave}>
            {editingId ? 'Güncelle' : 'Ekle'}
          </Button>
        </Box>

        <Divider />

        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Kayıtlı Hatırlatmalar
        </Typography>
        {loading ? (
          <CircularProgress size={24} />
        ) : reminders.length === 0 ? (
          <Typography color="text.secondary">Henüz hatırlatma eklenmedi.</Typography>
        ) : (
          <Stack spacing={1}>
            {reminders.map((r) => (
              <Paper key={r.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 600 }}>{r.teacher_name || r.teacher_id}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tip: {typeLabel(r.reminder_type || 'daily_activity')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sınıflar: {(r.class_names || []).join(', ') || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography> Saat: {formatTime(r)} ({TZ})</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Şablon: {r.template || 'daily_teacher_reminder_v2'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Günler: {(r.days || ["mon", "tue", "wed", "thu", "fri"]).join(', ')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Plan: {r.schedule_locked ? 'Sistem günü sabitler' : 'Gün seçimi açık'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={Boolean(r.enabled)}
                        onChange={(e) => handleToggle(r.id, e.target.checked)}
                        disabled={saving}
                      />
                      <Typography variant="body2">{r.enabled ? 'Aktif' : 'Pasif'}</Typography>
                    </Box>
                  </Grid>
                 <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <IconButton onClick={() => startEdit(r)} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => { setDeleteId(r.id); setDeleteDialogOpen(true); }} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        <Box>
          <Typography variant="body2" color="text.secondary">
            Gönderim şablonu: {selectedReminderMeta.template} • Zaman dilimi: {TZ} • Hatırlatmalar yalnızca saat başı planlanır.
          </Typography>
        </Box>
      </Stack>

      <Dialog open={editDialogOpen} onClose={resetForm} fullWidth maxWidth="sm">
        <DialogTitle>Hatırlatmayı {editingId ? 'Güncelle' : 'Ekle'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Hatırlatma tipi"
              value={reminderType}
              SelectProps={{ native: true }}
              onChange={(e) => {
                const nextType = e.target.value;
                setReminderType(nextType);
                const nextMeta = REMINDER_TYPES.find((item) => item.key === nextType);
                if (nextMeta?.scheduleLocked) {
                  setSelectedDays(nextMeta.lockedDays || ['mon']);
                }
              }}
            >
              {REMINDER_TYPES.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              {selectedReminderMeta.help}
            </Typography>
            <Autocomplete
              options={teacherOptions}
              value={selectedTeacher}
              onChange={(_, val) => setSelectedTeacher(val)}
              getOptionLabel={(opt) => opt?.label || ''}
              renderInput={(params) => <TextField {...params} label="Öğretmen seçin" />}
            />
            <TextField
              select
              label="Saat (sadece saat başı)"
              value={hour}
              SelectProps={{ native: true }}
              onChange={(e) => setHour(e.target.value)}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {h}:00
                </option>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <Typography>Hatırlatmayı etkinleştir</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {DAY_OPTIONS.map((d) => {
                const active = effectiveSelectedDays.includes(d.key);
                return (
                  <Chip
                    key={d.key}
                    label={d.label}
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    disabled={scheduleLocked}
                    onClick={() => {
                      setSelectedDays((prev) =>
                        prev.includes(d.key) ? prev.filter((x) => x !== d.key) : [...prev, d.key]
                      );
                    }}
                  />
                );
              })}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" disabled={scheduleLocked} onClick={() => setSelectedDays(["mon", "tue", "wed", "thu", "fri"])}>
                Hafta içi
              </Button>
              <Button variant="outlined" size="small" disabled={scheduleLocked} onClick={() => setSelectedDays(["mon", "tue", "wed", "thu", "fri", "sat", "sun"])}>
                Tüm hafta
              </Button>
            </Stack>
            {scheduleLocked && (
              <Alert severity="info">
                Bu hatırlatma tipi sadece Pazartesi günü çalışır.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm}>Vazgeç</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {editingId ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Hatırlatmayı sil</DialogTitle>
        <DialogContent dividers>
          <Typography>Bu hatırlatmayı silmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Vazgeç</Button>
          <Button
            onClick={() => deleteId && handleDelete(deleteId)}
            variant="contained"
            color="error"
            disabled={saving}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
